/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
// @mui
import {
    Box,
    Card,
    Stack,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Collapse,
    Divider,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    alpha,
} from '@mui/material';
// components
import { Iconify } from '../../../components';
import { NotFound } from '../../../components/empty-content';
// redux
import { useSelector, useDispatch } from '../../../redux/store';
import { revokeConsentRequest, getLeadConsentRequests } from '../../../redux/slices/consentV2';
// theme
import { common } from '../../../theme/palette';

// ─── Date parsing helper ───
// Backend returns dates as "DD-MM-YYYY HH:mm:ss.SSSSSSZ" (UTC)
const parseApiDate = (dateStr) => {
    if (!dateStr) return null;
    // Strip microseconds (.SSSSSS) but keep the Z timezone indicator
    const cleaned = dateStr.replace(/\.\d+/, '');
    const parsed = moment.utc(cleaned, 'DD-MM-YYYY HH:mm:ssZ');
    if (parsed.isValid()) return parsed.local();
    // Fallback to ISO format
    const iso = moment(dateStr);
    return iso.isValid() ? iso : null;
};

const formatDate = (dateStr) => {
    const m = parseApiDate(dateStr);
    return m ? m.format('DD MMM YYYY, h:mm A') : '—';
};

const timeSince = (dateStr) => {
    const m = parseApiDate(dateStr);
    return m ? m.fromNow() : '';
};

const getExpiryText = (expiresAt) => {
    const m = parseApiDate(expiresAt);
    if (!m) return null;
    const now = moment();
    if (m.isBefore(now)) return { text: 'Expired', color: '#919EAB' };
    const diffHours = m.diff(now, 'hours', true);
    if (diffHours < 1) {
        const mins = Math.ceil(m.diff(now, 'minutes', true));
        return { text: `Expires in ${mins}m`, color: '#FF4842' };
    }
    if (diffHours < 24) {
        const hrs = Math.floor(diffHours);
        return { text: `Expires in ${hrs}h`, color: '#FFAB00' };
    }
    const days = Math.floor(m.diff(now, 'days', true));
    return { text: `Expires in ${days}d`, color: '#078DEE' };
};

// ─── Status config ───
const STATUS_CONFIG = {
    SUBMITTED: { label: 'Submitted', color: 'success', icon: 'eva:checkmark-circle-2-fill', bgHex: '#00AB55' },
    PENDING: { label: 'Pending', color: 'warning', icon: 'eva:clock-fill', bgHex: '#FFAB00' },
    OPENED: { label: 'Opened', color: 'info', icon: 'eva:eye-fill', bgHex: '#078DEE' },
    EXPIRED: { label: 'Expired', color: 'default', icon: 'eva:alert-circle-fill', bgHex: '#919EAB' },
    REVOKED: { label: 'Revoked', color: 'error', icon: 'eva:close-circle-fill', bgHex: '#FF4842' },
};

const getStatusConfig = (status) => STATUS_CONFIG[status] || { label: status || 'Unknown', color: 'default', icon: 'eva:question-mark-circle-fill', bgHex: '#919EAB' };

// ─── Single consent request card ───
function ConsentRequestCard({ request, onRevoke }) {
    const [expanded, setExpanded] = useState(false);
    const config = getStatusConfig(request.status);

    const formName = request.form_name || request.form?.name || 'Consent Form';
    const submittedAt = request.submitted_at || request.updated_at;
    const pdfUrl = request.pdf_file || request.submission?.pdf_file;
    const createdBy = request.created_by
        ? `${request.created_by.first_name || ''} ${request.created_by.last_name || ''}`.trim()
        : null;

    return (
        <Card
            sx={{
                p: 0,
                mb: 1.5,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                overflow: 'hidden',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                '&:hover': { borderColor: config.bgHex, boxShadow: `0 0 0 1px ${config.bgHex}20` },
            }}
        >
            {/* Status accent bar */}
            <Box sx={{ height: 3, bgcolor: config.bgHex, opacity: 0.6 }} />

            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: (theme) => alpha(theme.palette[config.color]?.main || theme.palette.grey[500], 0.1),
                            flexShrink: 0,
                        }}
                    >
                        <Iconify
                            icon={config.icon}
                            sx={{ width: 22, height: 22, color: `${config.color}.main` }}
                        />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.4, color: 'text.primary' }}>
                            {formName}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.5, mt: 0.25 }}>
                            {request.status === 'SUBMITTED'
                                ? `Submitted ${timeSince(submittedAt)}`
                                : request.status === 'OPENED'
                                    ? `Opened ${timeSince(request.first_opened_at || request.created_at)}`
                                    : `Sent ${timeSince(request.created_at)}`
                            }
                            {createdBy ? ` by ${createdBy}` : ''}
                        </Typography>
                        {request.expires_at && (request.status === 'PENDING' || request.status === 'OPENED') && (() => {
                            const expiry = getExpiryText(request.expires_at);
                            return expiry ? (
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: expiry.color, mt: 0.25 }}>
                                    {expiry.text}
                                </Typography>
                            ) : null;
                        })()}
                    </Box>

                    <Chip
                        label={config.label}
                        size="small"
                        sx={{
                            fontSize: '11px',
                            height: 26,
                            fontWeight: 600,
                            bgcolor: (theme) => alpha(theme.palette[config.color]?.main || theme.palette.grey[500], 0.1),
                            color: `${config.color}.dark`,
                            border: 'none',
                        }}
                    />

                    {pdfUrl && (
                        <Tooltip title="Download signed PDF">
                            <IconButton
                                size="small"
                                onClick={() => window.open(pdfUrl, '_blank')}
                                sx={{
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                    '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16) },
                                }}
                            >
                                <Iconify icon="eva:download-fill" sx={{ width: 18, height: 18, color: 'primary.main' }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {(request.status === 'PENDING' || request.status === 'OPENED') && onRevoke && (
                        <Tooltip title="Revoke access">
                            <IconButton
                                size="small"
                                onClick={() => onRevoke(request)}
                                sx={{
                                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                                    '&:hover': { bgcolor: (theme) => alpha(theme.palette.error.main, 0.16) },
                                }}
                            >
                                <Iconify icon="eva:slash-fill" sx={{ width: 18, height: 18, color: 'error.main' }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip title={expanded ? 'Less details' : 'More details'}>
                        <IconButton
                            size="small"
                            onClick={() => setExpanded(!expanded)}
                            sx={{
                                transition: 'transform 0.2s',
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                        >
                            <Iconify icon="eva:chevron-down-fill" sx={{ width: 18, height: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Collapse in={expanded}>
                    <Divider sx={{ my: 1.5 }} />
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 1.5,
                            p: 1.5,
                            borderRadius: '8px',
                            bgcolor: 'background.neutral',
                        }}
                    >
                        <DetailRow label="Sent on" value={formatDate(request.created_at)} icon="eva:paper-plane-fill" />
                        {request.first_opened_at && (
                            <DetailRow label="First opened" value={formatDate(request.first_opened_at)} icon="eva:eye-fill" />
                        )}
                        {request.submitted_at && (
                            <DetailRow label="Submitted" value={formatDate(request.submitted_at)} icon="eva:checkmark-circle-2-fill" />
                        )}
                        {request.expires_at && (
                            <DetailRow label="Expires" value={formatDate(request.expires_at)} icon="eva:clock-fill" />
                        )}
                        {createdBy && (
                            <DetailRow label="Sent by" value={createdBy} icon="eva:person-fill" />
                        )}
                        {request.submission_id && (
                            <DetailRow label="Submission ID" value={`#${request.submission_id}`} icon="eva:hash-fill" />
                        )}
                    </Box>

                    {request.access_token && request.status !== 'SUBMITTED' && request.status !== 'EXPIRED' && request.status !== 'REVOKED' && (
                        <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Iconify icon="eva:external-link-fill" sx={{ width: 16, height: 16 }} />}
                                onClick={() => {
                                    const url = `${window.location.origin}/consent/${request.access_token}`;
                                    window.open(url, '_blank');
                                }}
                                sx={{ fontSize: '12px', textTransform: 'none', borderRadius: '8px' }}
                            >
                                Open consent link
                            </Button>
                            <Button
                                size="small"
                                variant="text"
                                startIcon={<Iconify icon="eva:copy-fill" sx={{ width: 16, height: 16 }} />}
                                onClick={() => {
                                    const url = `${window.location.origin}/consent/${request.access_token}`;
                                    navigator.clipboard.writeText(url);
                                }}
                                sx={{ fontSize: '12px', textTransform: 'none', borderRadius: '8px', color: 'text.secondary' }}
                            >
                                Copy link
                            </Button>
                        </Box>
                    )}

                    {request.error_message && (
                        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '8px', bgcolor: (theme) => alpha(theme.palette.error.main, 0.06), display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Iconify icon="eva:alert-triangle-fill" sx={{ width: 16, height: 16, color: 'error.main', mt: 0.25, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', color: 'error.main', fontWeight: 500 }}>
                                {request.error_message}
                            </Typography>
                        </Box>
                    )}
                </Collapse>
            </Box>
        </Card>
    );
}

ConsentRequestCard.propTypes = {
    request: PropTypes.object.isRequired,
    onRevoke: PropTypes.func,
};

// ─── Detail row helper ───
function DetailRow({ label, value, icon }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon && (
                <Iconify icon={icon} sx={{ width: 14, height: 14, color: 'text.disabled', flexShrink: 0 }} />
            )}
            <Box>
                <Typography sx={{ fontSize: '11px', color: 'text.disabled', lineHeight: 1.3 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 500, lineHeight: 1.4, color: 'text.primary' }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );
}

DetailRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
    icon: PropTypes.string,
};

// ─── Section header ───
function SectionHeader({ title, count, icon, color }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 0.5 }}>
            <Iconify icon={icon} sx={{ width: 16, height: 16, color }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.secondary' }}>
                {title}
            </Typography>
            <Chip
                label={count}
                size="small"
                sx={{
                    fontSize: '11px',
                    height: 20,
                    minWidth: 20,
                    fontWeight: 700,
                    bgcolor: `${color}14`,
                    color,
                }}
            />
        </Box>
    );
}

SectionHeader.propTypes = {
    title: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    icon: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
};

// ─── Main tab component ───
// eslint-disable-next-line no-unused-vars
export default function LeadDetailConsent({ leadDetail }) {
    const dispatch = useDispatch();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { leadConsentRequests, leadConsentLoading } = useSelector((state) => state.consentV2);
    const requests = leadConsentRequests?.results || [];

    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [revokeLoading, setRevokeLoading] = useState(false);

    const handleRevokeClick = (req) => {
        setRevokeTarget(req);
        setRevokeDialogOpen(true);
    };

    const handleRevokeConfirm = () => {
        if (!revokeTarget || !practiceId) return;
        setRevokeLoading(true);
        const leadId = leadDetail?.id;
        dispatch(revokeConsentRequest(practiceId, revokeTarget.id, (result) => {
            setRevokeLoading(false);
            setRevokeDialogOpen(false);
            setRevokeTarget(null);
            if (result.success && leadId) {
                dispatch(getLeadConsentRequests(practiceId, leadId));
            }
        }));
    };

    const handleRevokeCancel = () => {
        setRevokeDialogOpen(false);
        setRevokeTarget(null);
    };

    if (leadConsentLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8, flexDirection: 'column', gap: 1.5 }}>
                <CircularProgress size={28} sx={{ color: common.mintyZest }} />
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Loading consent forms...</Typography>
            </Box>
        );
    }

    if (!requests.length) {
        return (
            <Box sx={{ py: 4 }}>
                <NotFound />
                <Typography sx={{ textAlign: 'center', fontSize: '13px', color: 'text.secondary', mt: 1 }}>
                    No consent forms have been sent to this lead yet.
                </Typography>
            </Box>
        );
    }

    const submitted = requests.filter((r) => r.status === 'SUBMITTED');
    const pending = requests.filter((r) => r.status === 'PENDING' || r.status === 'OPENED');
    const other = requests.filter((r) => r.status !== 'SUBMITTED' && r.status !== 'PENDING' && r.status !== 'OPENED');

    return (
        <Stack spacing={3} sx={{ py: 1.5, pr: { xs: 0, md: '80px' } }}>
            {submitted.length > 0 && (
                <Box>
                    <SectionHeader title="Submitted" count={submitted.length} icon="eva:checkmark-circle-2-fill" color="#00AB55" />
                    {submitted.map((req) => (
                        <ConsentRequestCard key={req.id || req.access_token} request={req} />
                    ))}
                </Box>
            )}

            {pending.length > 0 && (
                <Box>
                    <SectionHeader title="Awaiting Response" count={pending.length} icon="eva:clock-fill" color="#FFAB00" />
                    {pending.map((req) => (
                        <ConsentRequestCard key={req.id || req.access_token} request={req} onRevoke={handleRevokeClick} />
                    ))}
                </Box>
            )}

            {other.length > 0 && (
                <Box>
                    <SectionHeader title="Expired / Revoked" count={other.length} icon="eva:close-circle-fill" color="#919EAB" />
                    {other.map((req) => (
                        <ConsentRequestCard key={req.id || req.access_token} request={req} />
                    ))}
                </Box>
            )}

            {/* Revoke confirmation dialog */}
            <Dialog
                open={revokeDialogOpen}
                onClose={handleRevokeCancel}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    Revoke Consent Access
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to revoke access to this consent form?
                        {revokeTarget && (
                            <Box component="span" sx={{ display: 'block', mt: 1, fontWeight: 600, color: 'text.primary' }}>
                                {revokeTarget.form_name || revokeTarget.form?.name || 'Consent Form'}
                            </Box>
                        )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        The patient will no longer be able to access or submit this consent form.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleRevokeCancel} disabled={revokeLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleRevokeConfirm}
                        disabled={revokeLoading}
                        startIcon={revokeLoading ? <CircularProgress size={16} color="inherit" /> : <Iconify icon="eva:slash-fill" width={18} />}
                        sx={{ borderRadius: '10px' }}
                    >
                        Revoke
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

LeadDetailConsent.propTypes = {
    leadDetail: PropTypes.object,
};
